import { format, parseISO } from 'date-fns';
import { formatInTimeZone, toZonedTime, fromZonedTime } from 'date-fns-tz';

/**
 * Common timezones for travel destinations
 */
export const COMMON_TIMEZONES = [
  { value: 'Europe/Dublin', label: 'Dublin (GMT/IST)' },
  { value: 'Europe/London', label: 'London (GMT/BST)' },
  { value: 'Europe/Paris', label: 'Paris (CET/CEST)' },
  { value: 'Europe/Berlin', label: 'Berlin (CET/CEST)' },
  { value: 'Europe/Rome', label: 'Rome (CET/CEST)' },
  { value: 'Europe/Madrid', label: 'Madrid (CET/CEST)' },
  { value: 'Europe/Amsterdam', label: 'Amsterdam (CET/CEST)' },
  { value: 'Europe/Lisbon', label: 'Lisbon (WET/WEST)' },
  { value: 'Europe/Athens', label: 'Athens (EET/EEST)' },
  { value: 'America/New_York', label: 'New York (EST/EDT)' },
  { value: 'America/Chicago', label: 'Chicago (CST/CDT)' },
  { value: 'America/Denver', label: 'Denver (MST/MDT)' },
  { value: 'America/Los_Angeles', label: 'Los Angeles (PST/PDT)' },
  { value: 'America/Toronto', label: 'Toronto (EST/EDT)' },
  { value: 'America/Mexico_City', label: 'Mexico City (CST/CDT)' },
  { value: 'America/Sao_Paulo', label: 'São Paulo (BRT)' },
  { value: 'America/Buenos_Aires', label: 'Buenos Aires (ART)' },
  { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
  { value: 'Asia/Seoul', label: 'Seoul (KST)' },
  { value: 'Asia/Shanghai', label: 'Shanghai (CST)' },
  { value: 'Asia/Hong_Kong', label: 'Hong Kong (HKT)' },
  { value: 'Asia/Singapore', label: 'Singapore (SGT)' },
  { value: 'Asia/Bangkok', label: 'Bangkok (ICT)' },
  { value: 'Asia/Dubai', label: 'Dubai (GST)' },
  { value: 'Asia/Kolkata', label: 'Mumbai/Delhi (IST)' },
  { value: 'Australia/Sydney', label: 'Sydney (AEST/AEDT)' },
  { value: 'Australia/Melbourne', label: 'Melbourne (AEST/AEDT)' },
  { value: 'Pacific/Auckland', label: 'Auckland (NZST/NZDT)' },
  { value: 'Africa/Cairo', label: 'Cairo (EET)' },
  { value: 'Africa/Johannesburg', label: 'Johannesburg (SAST)' },
  { value: 'UTC', label: 'UTC' },
];

/**
 * Format a date in a specific timezone
 */
export function formatInTz(date, timezone, formatStr = 'PPp') {
  if (!date) return '';
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  return formatInTimeZone(dateObj, timezone, formatStr);
}

/**
 * Format time only in a specific timezone
 */
export function formatTimeInTz(date, timezone) {
  return formatInTz(date, timezone, 'HH:mm');
}

/**
 * Format date only in a specific timezone
 */
export function formatDateInTz(date, timezone) {
  return formatInTz(date, timezone, 'EEE, d MMM yyyy');
}

/**
 * Get time display in a specific timezone
 * The ISO date is stored in UTC, this converts it for display in any timezone
 */
export function getTimeInTimezone(isoDate, displayTimezone) {
  if (!isoDate) return '';
  const dateObj = typeof isoDate === 'string' ? parseISO(isoDate) : isoDate;
  return formatInTimeZone(dateObj, displayTimezone, 'HH:mm');
}

/**
 * Get both home and local timezone display strings
 * @param date - ISO date string (stored in UTC)
 * @param localTimezone - The timezone the event is in
 * @param homeTimezone - The user's home timezone
 */
export function getDualTimezoneDisplay(date, localTimezone, homeTimezone) {
  if (!date) return { local: '', home: '' };
  
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  
  return {
    local: formatInTimeZone(dateObj, localTimezone, 'HH:mm'),
    home: formatInTimeZone(dateObj, homeTimezone, 'HH:mm'),
    localFull: formatInTimeZone(dateObj, localTimezone, 'EEE, d MMM HH:mm'),
    homeFull: formatInTimeZone(dateObj, homeTimezone, 'EEE, d MMM HH:mm'),
  };
}

/**
 * Get timezone abbreviation
 */
export function getTimezoneAbbr(timezone, date = new Date()) {
  return formatInTimeZone(date, timezone, 'zzz');
}

/**
 * Calculate timezone offset difference in hours
 */
export function getTimezoneOffset(timezone1, timezone2, date = new Date()) {
  const date1 = toZonedTime(date, timezone1);
  const date2 = toZonedTime(date, timezone2);
  const diff = (date1.getTime() - date2.getTime()) / (1000 * 60 * 60);
  return diff;
}

/**
 * Create an ISO datetime string from date and time inputs in a specific timezone
 * 
 * This is the KEY function for saving events correctly:
 * - User enters "14:00" and selects "Singapore" timezone
 * - This function converts that to the equivalent UTC time
 * - The stored ISO string represents the moment in time when it's 14:00 in Singapore
 * 
 * @param dateStr - Date in YYYY-MM-DD format
 * @param timeStr - Time in HH:mm format
 * @param timezone - The timezone the user intended (e.g., 'Asia/Singapore')
 * @returns ISO string in UTC
 */
export function createISODateTime(dateStr, timeStr, timezone) {
  // Combine date and time into a local datetime string
  const localDateTimeStr = `${dateStr}T${timeStr}:00`;
  
  // fromZonedTime takes a date that represents local time in the given timezone
  // and returns the equivalent UTC date
  // 
  // Example: If localDateTimeStr is "2026-01-15T14:00:00" and timezone is "Asia/Singapore"
  // Singapore is UTC+8, so this represents 06:00 UTC
  const utcDate = fromZonedTime(localDateTimeStr, timezone);
  
  return utcDate.toISOString();
}

/**
 * Extract date and time parts from ISO string for form inputs
 * Displays the time as it would appear in the specified timezone
 * 
 * @param isoString - ISO datetime string (stored in UTC)
 * @param timezone - The timezone to display the time in
 * @returns Object with date (YYYY-MM-DD) and time (HH:mm) in the specified timezone
 */
export function parseISOForForm(isoString, timezone) {
  if (!isoString) return { date: '', time: '' };
  
  const dateObj = parseISO(isoString);
  
  return {
    date: formatInTimeZone(dateObj, timezone, 'yyyy-MM-dd'),
    time: formatInTimeZone(dateObj, timezone, 'HH:mm'),
  };
}
